import { ref, watch } from 'vue';
import { ElMessage } from 'element-plus';
import { trackWidgetEvent } from '@/utils/widget-analytics';
import { isEmittableCompany } from '@/components/widget/results/results-view.types';
import type {
  EmissionSuccessData,
  ResultsCompany,
  ResultsViewProps,
} from '@/components/widget/results/results-view.types';
import { dispatchExpand, maintainExpandMode } from './expandMode';
import type { QuoteSources, SelectedCompanyRef } from './types';

interface EmissionDeps {
  props: Readonly<ResultsViewProps>;
  sources: QuoteSources;
  selectedCompany: SelectedCompanyRef;
}

export function useEmissionState({ props, sources, selectedCompany }: EmissionDeps) {
  const showEmissionForm = ref(false);
  const showEmissionSuccess = ref(false);
  const emissionSuccessData = ref<EmissionSuccessData | null>(null);

  const handleEmitir = (company: ResultsCompany): void => {
    if (!isEmittableCompany(company)) {
      ElMessage.info('La emisión no está disponible para Chubb en este momento.');
      return;
    }
    trackWidgetEvent('add_to_cart', { tipo: props.allowEmission ? 'emitir' : 'me_interesa', aseguradora: company });
    selectedCompany.value = company;
    showEmissionForm.value = true;
    showEmissionSuccess.value = false;
    maintainExpandMode();
  };

  const handleEmissionSuccess = (data: EmissionSuccessData): void => {
    showEmissionForm.value = false;
    emissionSuccessData.value = data;
    showEmissionSuccess.value = true;
  };

  const handleBackFromSuccess = (): void => {
    showEmissionSuccess.value = false;
    emissionSuccessData.value = null;
  };

  watch(
    () => showEmissionForm.value,
    (isShowing) => {
      if (isShowing) {
        dispatchExpand('El formulario de emisión necesita más espacio');
      } else if (sources.quotesResponse.value) {
        dispatchExpand('Mantener modo expandido al volver a resultados');
      }
    }
  );

  return { showEmissionForm, showEmissionSuccess, emissionSuccessData, handleEmitir, handleEmissionSuccess, handleBackFromSuccess };
}
